import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ImagePreset, Content, Movie, Series, Episode, UseContentReturn, useMaxViewingDuration } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { ContentInfoWrapper } from './ContentInfoWrapper';
import { ContentInfoSubtitle } from './ContentInfoSubtitle';
import { ContentInfoTitle } from './ContentInfoTitle';
import { ContentInfoDescription } from './ContentInfoDescription';
import { ImageComponent } from '../ImageComponent';
import { breakpoints } from '../../styles/breakpoints';
import { useAvailableUntil } from '../../hooks/useAvailableUntil';
import { ExpandableText } from '../ExpandableText';
import { useRentedUntil } from '../../hooks/useRentedUntil';
import { useContentRating } from '../../hooks/useContentRating';

type ContentInfoComponentsProps = {
	content: Content;
};

const ContentInfoBackground = styled(ImageComponent)`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-repeat: no-repeat;
	background-size: cover;
	background-position: top center;
	background-attachment: fixed;
	z-index: -1;
	display: none;

	@media ${breakpoints.sm} {
		display: block;
	}
`;

const ContentInfoYear: React.FC<ContentInfoComponentsProps> = ({ content }) => {
	const { t } = useTranslation();

	const yearsString = useMemo(() => {
		if (!content) return null;

		if (content instanceof Movie) {
			const { year } = content;
			if (year) {
				return year;
			}
		}

		if (content instanceof Series) {
			const seasons = content?.seasons ?? [];

			const years = seasons
				.map((season) => season.year)
				.filter((year) => year > 0)
				.filter((year, index, self) => self.indexOf(year) === index);

			if (years.length > 0) {
				return years.length > 1 ? `${years[0]}-${years[years.length - 1]}` : years[0];
			}

			return null;
		}

		return null;
	}, [content]);

	if (!yearsString) return null;

	return (
		<div className={'year'}>
			<span className={'title'}>{t('ContentInfo.Year')}</span>
			<span className={'data'}>{yearsString}</span>
		</div>
	);
};

const ContentInfoSubtitleComponent: React.FC<ContentInfoComponentsProps> = ({ content }) => {
	const { t } = useTranslation();

	const genresComponent = useMemo(() => {
		if (!content) return null;

		const { genres } = content;
		if (genres && genres.length > 0) {
			return (
				<div className={'genres'}>
					<span className={'title'}>
						{genres
							.slice(0, 3)
							.map((genre) => genre.title)
							.join(' / ')}
					</span>
				</div>
			);
		}

		return null;
	}, [content]);

	const imdbRatingComponent = useMemo(() => {
		if (!content) return null;

		const { imdbRating } = content;
		if (imdbRating > 0) {
			return (
				<div className={'imdbRating'}>
					<span className={'title'}>{t('ContentInfo.IMDb')}</span>
					<span className={'data'}>{imdbRating}</span>
				</div>
			);
		}

		return null;
	}, [content, t]);

	const { contentRating: fetchedRating } = useContentRating(content?.id || null);

	const contentRatingComponent = useMemo(() => {
		if (!content) return null;

		const rating = (content as any).contentRating || (content as any)._raw?.contentRating || fetchedRating;
		if (rating) {
			return (
				<div className={'contentRating'} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
					{rating.icon && <img src={rating.icon} alt={rating.name || ''} title={rating.name || ''} style={{ height: '24px', backgroundColor: 'white', borderRadius: '50%', border: '1px solid white' }} />}
					{rating.contentFlags
						?.slice() // Copy array before sorting
						.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
						.slice(0, 3) // Max 4 icons total (1 age rating + 3 flags)
						.map((flag: any) => (
							flag.icon && <img key={flag.identifier} src={flag.icon} alt={flag.name || ''} title={flag.name || ''} style={{ height: '24px', backgroundColor: 'white', borderRadius: '50%', border: '1px solid white' }} />
						))}
				</div>
			);
		}

		return null;
	}, [content, fetchedRating]);

	if (genresComponent || imdbRatingComponent || contentRatingComponent) {
		return (
			<ContentInfoSubtitle className={'subtitle'}>
				{genresComponent}
				{imdbRatingComponent}
				{contentRatingComponent}
			</ContentInfoSubtitle>
		);
	}

	return null;
};

type ContentInfoProps = Partial<UseContentReturn>;
type ContentInfoPropsWithChildren = ContentInfoProps & {
	children?: React.ReactNode;
};

const ContentInfo: React.FC<ContentInfoPropsWithChildren> = ({ content, children }) => {
	const { t } = useTranslation();

	const backdropImageUrl = useMemo(() => content && content.getBackdropImageUrl({ presets: [ImagePreset.BACKDROP, ImagePreset.BACKDROP_OVERLAY] }), [content]);

	const title = useMemo(() => {
		if (content instanceof Episode) {
			return `${content?.series?.title} - ${content?.title}`;
		}

		return content?.title ?? null;
	}, [content]);

	const description = content?.description ?? null;

	const maxViewingDuration = useMaxViewingDuration(content);
	const availableUntil = useAvailableUntil(content);
	const rentedUntil = useRentedUntil(content);

	if (!content) return null;

	return (
		<div className={'content-info'}>
			<ContentInfoBackground images={[backdropImageUrl]} />
			<ContentInfoWrapper>
				<ContentInfoTitle>
					<h1>{title}</h1>
				</ContentInfoTitle>
				<ContentInfoSubtitleComponent content={content} />
				<ContentInfoDescription>
					<ExpandableText numberOfLines={5}>{description}</ExpandableText>
				</ContentInfoDescription>
				<div className={'details'}>
					<div className={'details-container'}>
						<ContentInfoYear content={content} />
						{maxViewingDuration && !rentedUntil && (
							<div className={'viewing-duration'}>
								<span className={'title'}>{t('ContentInfo.MaxViewingDuration.Title')}</span>
								<span className={'data'}>{t('ContentInfo.MaxViewingDuration.Data', { maxViewingDuration: maxViewingDuration / 3600 })}</span>
							</div>
						)}
						{availableUntil && !rentedUntil && (
							<div className={'available-until'}>
								<span className={'title'}>{t('ContentInfo.AvailableUntil.Title')}</span>
								<span className={'data'}>{availableUntil}</span>
							</div>
						)}
						{rentedUntil && (
							<div className={'rented-until'}>
								<span className={'title'}>{t('ContentInfo.RentedUntil.Title')}</span>
								<span className={'data'}>{rentedUntil}</span>
							</div>
						)}
					</div>
				</div>
			</ContentInfoWrapper>
			{children}
		</div>
	);
};

export default ContentInfo;
